import { useCallback, useEffect, useState } from 'react';

// Cross-browser aliases (Safari / older WebKit still ship webkit-prefixed names).
type FsDoc = Document & {
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void> | void;
  webkitFullscreenEnabled?: boolean;
};
type FsEl = HTMLElement & {
  webkitRequestFullscreen?: () => Promise<void> | void;
};

const getFullscreenElement = (): Element | null => {
  if (typeof document === 'undefined') return null;
  const d = document as FsDoc;
  return document.fullscreenElement ?? d.webkitFullscreenElement ?? null;
};

const isInIframe = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
};

const isFullscreenSupported = (): boolean => {
  if (typeof document === 'undefined') return false;
  if (isInIframe()) return false;
  const d = document as FsDoc;
  return d.fullscreenEnabled ?? d.webkitFullscreenEnabled ?? false;
};

interface UseFullscreenResult {
  isFullscreen: boolean;
  isSupported: boolean;
  toggle: () => Promise<void>;
}

/**
 * Drives the browser Fullscreen API for the whole document. State is always derived from
 * `document.fullscreenElement` (kept in sync via the fullscreenchange event) so Esc / F11 /
 * tab-switch exits don't desync the UI. Fullscreen can only be entered from a user gesture,
 * so `toggle` must be called directly from an onClick (or other trusted-event) handler.
 */
export const useFullscreen = (): UseFullscreenResult => {
  const [isFullscreen, setIsFullscreen] = useState<boolean>(() => !!getFullscreenElement());
  const [isSupported] = useState<boolean>(() => isFullscreenSupported());

  useEffect(() => {
    const sync = () => setIsFullscreen(!!getFullscreenElement());
    document.addEventListener('fullscreenchange', sync);
    document.addEventListener('webkitfullscreenchange', sync);
    return () => {
      document.removeEventListener('fullscreenchange', sync);
      document.removeEventListener('webkitfullscreenchange', sync);
    };
  }, []);

  const toggle = useCallback(async () => {
    try {
      if (getFullscreenElement()) {
        const d = document as FsDoc;
        await (document.exitFullscreen?.() ?? d.webkitExitFullscreen?.());
      } else {
        const el = document.documentElement as FsEl;
        await (el.requestFullscreen?.() ?? el.webkitRequestFullscreen?.());
      }
      // No setState here — the fullscreenchange listener is the single source of truth,
      // so every exit path (button, Esc, tab-switch) updates the UI uniformly.
    } catch {
      // Denied / not a trusted gesture (NotAllowedError, TypeError). Fail silently.
    }
  }, []);

  return { isFullscreen, isSupported, toggle };
};
